import { QueryClientProvider } from '@tanstack/react-query';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { lazy, StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider } from '@/features/auth/auth-context';
import { CommandPaletteProvider } from '@/features/command-palette/command-context';
import { SearchProvider } from '@/features/search/search-context';
import { ThemeProvider } from '@/features/theme/theme-context';
import { queryClient } from '@/lib/query-client';
import { routeTree } from './routeTree.gen';
import './styles.css';

const ReactQueryDevtools = import.meta.env.DEV
  ? lazy(() =>
      import('@tanstack/react-query-devtools').then((m) => ({
        default: m.ReactQueryDevtools,
      })),
    )
  : null;

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
  scrollRestoration: true,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Root element #root not found');

createRoot(rootEl).render(
  <StrictMode>
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <SearchProvider>
            <CommandPaletteProvider>
              <RouterProvider router={router} />
            </CommandPaletteProvider>
          </SearchProvider>
        </AuthProvider>
        {ReactQueryDevtools && (
          <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
        )}
      </QueryClientProvider>
      <Toaster richColors closeButton position="bottom-right" />
    </ThemeProvider>
  </StrictMode>,
);
